import {
  taskTypeOptions,
  taskTypeToAdmissionDocumentSlug,
  type TaskTypeValue,
} from "@/src/constants/documentPreparationPopUpConsts";
import { admissionsOfficeHeaderTabsConst } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";

export type AdmissionOfficeCountry = "ukraine" | "malta" | "poland";
export type AdmissionOfficeLang = "en" | "fr" | "de";

export interface AdmissionOfficeRouteFromTask {
  country: AdmissionOfficeCountry;
  document: string;
  lang: AdmissionOfficeLang;
  pathname: string;
}

const OFFICE_TO_COUNTRY: Record<string, AdmissionOfficeCountry> = {
  UKRAINE: "ukraine",
  MALTA: "malta",
  POLAND: "poland",
};

const buildTaskTypeLookup = (): Map<string, string> => {
  const lookup = new Map<string, string>();

  const register = (key: string, slug: string) => {
    const normalized = key.trim().toLowerCase();
    if (!normalized) return;
    lookup.set(normalized, slug);
  };

  (Object.entries(taskTypeToAdmissionDocumentSlug) as [TaskTypeValue, string][]).forEach(([taskType, slug]) => {
    register(taskType, slug);
    const option = taskTypeOptions.find((item) => item.value === taskType);
    if (option) register(option.label, slug);
  });

  return lookup;
};

const TASK_TYPE_LOOKUP = buildTaskTypeLookup();

const getDocumentsForCountry = (country: AdmissionOfficeCountry): string[] => {
  const tab = admissionsOfficeHeaderTabsConst.find((item) => item.id === country);
  if (!tab) return ["admission"];
  return Array.from(new Set(tab.mass.map((option) => option.slug)));
};

export const normalizeTaskOffice = (office: string | undefined, fallbackOffice = "MALTA"): string => {
  const upper = (office?.trim() || fallbackOffice).toUpperCase();
  return upper === "ALL" ? "MALTA" : upper;
};

export const officeToAdmissionCountry = (
  office: string | undefined,
  fallbackOffice = "MALTA",
): AdmissionOfficeCountry => {
  const normalized = normalizeTaskOffice(office, fallbackOffice);
  return OFFICE_TO_COUNTRY[normalized] ?? "malta";
};

const normalizeTaskTypeKey = (type: string | undefined): TaskTypeValue | "" => {
  if (!type) return "";
  const trimmed = type.trim();
  const byValue = taskTypeOptions.find((item) => item.value === trimmed);
  if (byValue) return byValue.value;
  const byLabel = taskTypeOptions.find((item) => item.label === trimmed);
  if (byLabel) return byLabel.value;
  const lower = trimmed.toLowerCase();
  const byLabelLower = taskTypeOptions.find(
    (item) => item.label.toLowerCase() === lower || item.value.toLowerCase() === lower,
  );
  return byLabelLower?.value ?? "";
};

export const taskTypeToDocumentSlug = (type: string | undefined): string => {
  const taskTypeKey = normalizeTaskTypeKey(type);
  if (!taskTypeKey) return taskTypeToAdmissionDocumentSlug["admission-letter"];

  const fromCanonical = taskTypeToAdmissionDocumentSlug[taskTypeKey];
  if (fromCanonical) return fromCanonical;

  const fromLookup = TASK_TYPE_LOOKUP.get(taskTypeKey) ?? TASK_TYPE_LOOKUP.get(type?.trim().toLowerCase() ?? "");
  return fromLookup ?? taskTypeToAdmissionDocumentSlug["admission-letter"];
};

const pickDocumentSlugForCountry = (requestedSlug: string, country: AdmissionOfficeCountry): string => {
  if (requestedSlug === "qr-code") return requestedSlug;

  const available = getDocumentsForCountry(country);
  if (available.includes(requestedSlug)) return requestedSlug;
  return "admission";
};

export const buildAdmissionOfficePathname = (
  country: AdmissionOfficeCountry,
  document: string,
  lang: AdmissionOfficeLang = "en",
): string => {
  if (document === "qr-code") return "/documents-qr-code";
  if (lang === "fr" || lang === "de") {
    return `/admissions-office/${lang}/${country}/${document}`;
  }
  return `/admissions-office/${country}/${document}`;
};

/**
 * Resolves Admissions Office URL from task `type` and office (`applicant_info.form_office`).
 */
export const resolveAdmissionOfficeRouteFromTask = (
  taskType: string | undefined,
  office: string | undefined,
  fallbackOffice = "MALTA",
): AdmissionOfficeRouteFromTask => {
  const country = officeToAdmissionCountry(office, fallbackOffice);
  const requestedSlug = taskTypeToDocumentSlug(taskType);
  const document = pickDocumentSlugForCountry(requestedSlug, country);
  const lang: AdmissionOfficeLang = "en";

  return {
    country,
    document,
    lang,
    pathname: buildAdmissionOfficePathname(country, document, lang),
  };
};
